<script lang="ts">
  import {quintIn, quintOut} from 'svelte/easing';
  import {marked} from 'marked';
  import {Link} from 'yrv';

  import {
    Confidentiality,
    type Media,
    type ImageMedia,
    ProjectType
  } from '../../types/project';
  import type {DateRange} from '../../types/date';

  import {VIEW_PROJECT} from '../../constants/views';

  import {send, receive} from '../../utils/crossfade';
  import {isValidDate} from '../../utils/date-utils';

  import fallbackImage from '../../assets/no-media-project.png';

  import ConfidentialBadge from '../confidential-badge/ConfidentialBadge.svelte';
  import DraftBadge from '../draft-badge/DraftBadge.svelte';

  marked.setOptions({breaks: true});

  export let id: string;
  export let type: ProjectType;
  export let isDraft: boolean;
  export let title: string;
  export let confidentiality: Confidentiality | undefined;
  export let customers: Array<string> | undefined;
  export let description: string | undefined;
  export let media: Array<Media> | undefined;
  export let creationPeriod: DateRange | undefined;

  $: images = (media || []).filter<ImageMedia>(
    (mediaEntry): mediaEntry is ImageMedia => mediaEntry.type === 'image'
  );
  $: coverImage = images.length > 0 ? images[0] : null;
  $: path = id && VIEW_PROJECT.replace(':id', id);
</script>

<style lang="postcss">
  :global(.projectCard),
  :global(.projectCard):hover,
  :global(.projectCard):focus,
  :global(.projectCard):visited {
    color: inherit;
    text-decoration: none;
  }

  .card {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    margin: auto;
    border-radius: var(--ubilabs-border-radius-medium);
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(var(--ubilabs-black-rgb), 0.14),
      0 1px 2px rgba(var(--ubilabs-black-rgb), 0.12);
    background-color: var(--ubilabs-white);
  }

  .card:hover,
  :global(.projectCard):focus-visible > .card {
    box-shadow: 0 2px 4px rgba(var(--ubilabs-black-rgb), 0.14),
      0 3px 4px rgba(var(--ubilabs-black-rgb), 0.12),
      0 1px 5px rgba(var(--ubilabs-black-rgb), 0.2);
  }

  .imageContainer {
    position: relative;
    width: 100%;
    border-bottom: 1px solid var(--ubilabs-gray-light);
    background-color: var(--ubilabs-gray-light);
    overflow: hidden;
  }

  .imageContainer.development {
    border-top: var(--ubilabs-border-radius-medium) solid var(--ubilabs-green);
  }
  .imageContainer.consulting {
    border-top: var(--ubilabs-border-radius-medium) solid var(--ubilabs-yellow);
  }

  .imageContainer::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      rgba(var(--ubilabs-black-rgb), 0.1),
      transparent 40%
    );
  }

  .imageContainer > svg {
    width: auto;
    height: auto;
  }

  .imageContainer > img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .imageContainer .badges {
    z-index: 2;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    gap: 0.25rem;
    margin: 0.75rem;
    margin-right: 2.5rem;
  }

  .textContainer {
    display: grid;
    grid-template-rows: auto auto auto 1fr;
    min-height: 12rem;
    max-height: 16rem;
    padding: 1rem 1.5rem;
  }

  .projectPeriod {
    display: flex;
    margin-bottom: 0.5rem;
    color: var(--ubilabs-gray-dark-2);
    white-space: pre-wrap;
  }

  .projectCustomers {
    color: var(--ubilabs-gray-dark-1);
    font-size: var(--ubilabs-heading-2-font-size);
    font-weight: var(--ubilabs-heading-font-weight);
  }

  .projectTitle {
    margin-bottom: 0.5rem;
    font-size: var(--ubilabs-heading-2-font-size);
    font-weight: var(--ubilabs-heading-font-weight);
  }

  .projectDescription {
    grid-row: 4;
    display: block;
    overflow: hidden;
    background: linear-gradient(
          rgba(var(--scroll-container-background-color-rgb), 0),
          rgb(var(--scroll-container-background-color-rgb)) 70%
        )
        center bottom,
      radial-gradient(
          farthest-side at 50% 100%,
          rgba(var(--scroll-container-shadow-color-rgb), 0.3),
          rgba(var(--scroll-container-shadow-color-rgb), 0)
        )
        center bottom;
    background-repeat: no-repeat;
    background-size: 100% calc(var(--scroll-container-shadow-size) * 2),
      100% var(--scroll-container-shadow-size);
    background-attachment: local, scroll;
  }

  /* Override markdown styles in card description */
  .projectDescription :global(*) {
    margin: 0;
    padding: 0;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
  .projectDescription :global(*)::before,
  .projectDescription :global(*)::after {
    display: none;
  }

  .missingData {
    color: var(--ubilabs-gray-dark-1);
  }
</style>

<Link class="projectCard" href={path}>
  <div
    class="card"
    in:receive={{key: id, easing: quintOut, delay: 200}}
    out:send|local={{key: id, easing: quintIn}}>
    <div class="imageContainer {type}">
      <!-- SVG placeholder to keep an aspect ratio of 16:9 -->
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 9" />
      <img
        style="object-position:
          {coverImage?.focusPoint ? coverImage.focusPoint.x * 100 : 50}%
          {coverImage?.focusPoint ? coverImage.focusPoint.y * 100 : 50}%;"
        src={coverImage?.url || fallbackImage}
        alt={title}
        loading="lazy" />

      <div class="badges">
        {#if isDraft}
          <DraftBadge />
        {/if}

        {#if confidentiality === Confidentiality.CONFIDENTIAL}
          <ConfidentialBadge />
        {/if}
      </div>
    </div>

    <div class="textContainer">
      <div class="projectPeriod">
        {#if creationPeriod}
          {#if !creationPeriod.end}Since{/if}
          {#if creationPeriod.start && isValidDate(creationPeriod.start)}
            <time datetime={creationPeriod.start.toISOString().split('T')[0]}>
              {creationPeriod.start.toLocaleDateString(undefined, {
                month: 'short',
                year: 'numeric'
              })}
            </time>
          {/if}
          {#if creationPeriod.end && isValidDate(creationPeriod.end)}
            <span>&nbsp;–&nbsp;</span>
            <time datetime={creationPeriod.end.toISOString().split('T')[0]}>
              {creationPeriod.end.toLocaleDateString(undefined, {
                month: 'short',
                year: 'numeric'
              })}
            </time>
          {/if}
        {:else}
          <span class="missingData">Unknown creation period</span>
        {/if}
      </div>
      {#if customers?.length}
        <div class="projectCustomers">
          {customers.join(', ')}:
        </div>
      {/if}
      <div class="projectTitle">{title}</div>

      <p class="projectDescription">
        {#if description}
          {@html marked(description)}
        {/if}
      </p>
    </div>
  </div>
</Link>
